import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { User, Building2, Loader2, ArrowLeft, Calendar, Users, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'player',
    birthdate: '',
    gender: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (!formData.email && !formData.phone) {
      toast.error('E-Mail oder Telefonnummer erforderlich');
      return;
    }

    if (!formData.phone) {
      toast.error('Telefonnummer ist erforderlich');
      return;
    }

    if (!formData.birthdate) {
      toast.error('Geburtsdatum ist erforderlich');
      return;
    }

    if (!formData.gender) {
      toast.error('Geschlecht ist erforderlich');
      return;
    }
    
    setLoading(true);
    
    try {
      await register({
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        password: formData.password,
        role: formData.role,
        birthdate: formData.birthdate,
        gender: formData.gender,
      });
      toast.success('Konto erfolgreich erstellt!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 hex-pattern">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-tunis-blue hover:text-tunis-teal transition-colors">
        <ArrowLeft className="w-5 h-5" />
        Zurück
      </Link>
      
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt="Tunis Kick" className="h-20 w-20 mx-auto rounded-full shadow-lg" />
          </Link>
          <h1 className="mt-4 font-display font-bold text-2xl text-tunis-blue">Konto erstellen</h1>
          <p className="text-muted-foreground">Werde Teil der Tunis Kick Community</p>
        </div>

        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Registrieren</CardTitle>
            <CardDescription>Fülle das Formular aus, um loszulegen</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Role Selection */}
              <div className="space-y-3">
                <Label>Ich bin ein...</Label>
                <RadioGroup
                  value={formData.role}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
                  className="grid grid-cols-2 gap-4"
                >
                  <Label
                    htmlFor="role-player"
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.role === 'player'
                        ? 'border-tunis-teal bg-tunis-teal/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RadioGroupItem value="player" id="role-player" data-testid="role-player" />
                    <User className="w-5 h-5 text-tunis-blue" />
                    <span className="font-medium">Spieler</span>
                  </Label>
                  <Label
                    htmlFor="role-owner"
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.role === 'field_owner'
                        ? 'border-tunis-teal bg-tunis-teal/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RadioGroupItem value="field_owner" id="role-owner" data-testid="role-owner" />
                    <Building2 className="w-5 h-5 text-tunis-blue" />
                    <span className="font-medium">Feldbesitzer</span>
                  </Label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Dein Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="input-name"
                  className="h-12"
                />
              </div>

              {/* Birthdate and Gender Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birthdate">Geburtsdatum *</Label>
                  <Input
                    id="birthdate"
                    name="birthdate"
                    type="date"
                    value={formData.birthdate}
                    onChange={handleChange}
                    required
                    data-testid="input-birthdate"
                    className="h-12"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geschlecht *</Label>
                  <RadioGroup
                    value={formData.gender}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, gender: value }))}
                    className="grid grid-cols-2 gap-2 h-12"
                  >
                    <Label
                      htmlFor="gender-male"
                      className={`flex items-center justify-center gap-2 h-12 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.gender === 'male'
                          ? 'border-tunis-teal bg-tunis-teal/10 text-tunis-teal'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value="male" id="gender-male" data-testid="gender-male" className="sr-only" />
                      <span className="text-sm font-medium">Männlich</span>
                    </Label>
                    <Label
                      htmlFor="gender-female"
                      className={`flex items-center justify-center gap-2 h-12 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.gender === 'female'
                          ? 'border-tunis-teal bg-tunis-teal/10 text-tunis-teal'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value="female" id="gender-female" data-testid="gender-female" className="sr-only" />
                      <span className="text-sm font-medium">Weiblich</span>
                    </Label>
                  </RadioGroup>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="deine@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="input-email"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefonnummer *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+216 XX XXX XXX"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="input-phone"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Mindestens 6 Zeichen"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="input-password"
                    className="h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Passwort wiederholen"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="input-confirm-password"
                    className="h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary h-12"
                disabled={loading}
                data-testid="btn-register"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Konto erstellen'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Bereits registriert?{' '}
          <Link to="/login" className="font-semibold text-tunis-teal hover:underline" data-testid="link-login">
            Jetzt anmelden
          </Link>
        </p>
      </div>
    </div>
  );
}
